import React, { useRef, useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { LabeledInput } from '@components/atoms/Input/LabeledInput';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { useTranslation } from 'react-i18next';
import { useRegistrationFirstStep } from '@services/useRegistrationFirstStep';
import { useData } from '@hooks/useData';
import { Form } from '@components/atoms/Form';
import { useNotifications } from '@hooks/useNotifications';
import { Draft } from './../../../context/dataContext';
import { useNavigate } from 'react-router-dom';

export interface PersonalInfoData {
    uuid: string;
    name: string;
    email: string;
    provider: string;
    document_type: 'CC' | 'BI' | 'PASS' | 'CNH' | 'AR' | 'OT';
    docId: string;
    phone: string;
    nationality: string;
    address: string;
}

interface PersonalInfoDataErrors {
    name: string[];
    email: string[];
    document_type: string[];
    docId: string[];
    phone: string[];
    nationality: string[];
    address: string[];
}

interface Step1Data {
    name: string;
    email: string;
    provider: string;
    document_type: 'CC' | 'BI' | 'PASS' | 'CNH' | 'AR' | 'OT';
    document_number: string;
    phone_number: string;
    user_type_id: number;
    nationality: string;
    address: string;
}

interface FirstStepData {
    uuid: string;
    current_step: number;
    step_1: Step1Data;
    otp: { validated: boolean };
}

interface DataContextType {
    draft?: Draft | null;
    firstStep?: { data?: FirstStepData };
    setFirstStep?: (data: { data: FirstStepData }) => void;
    selectedRole?: { id: number };
    otpStep?: { verified: boolean; timestamp: number };
}

const documentTypeOptions = [
    { value: 'CC', text: 'CC' },
    { value: 'BI', text: 'BI' },
    { value: 'PASS', text: 'PASS' },
    { value: 'CNH', text: 'CNH' },
    { value: 'AR', text: 'AR' },
    { value: 'INS', text: 'INS' },
    { value: 'DUA', text: 'DUA' },
    { value: 'OT', text: 'Other' },
];

export const PersonalInfoStep: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);
    const { firstStep, setFirstStep, selectedRole } = useData() as DataContextType;
    const { setRegister } = useData();
    const { showDefaultNotification } = useNotifications();
    const { request: requestRegisterFirstStep, error, validationErrors, lastResponseTime } = useRegistrationFirstStep();
    const [errors, setErrors] = useState<PersonalInfoDataErrors>({
        name: [],
        email: [],
        document_type: [],
        docId: [],
        phone: [],
        nationality: [],
        address: [],
    });

    useEffect(() => {
        setRegister(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run on mount

    // handles stories
    const context = useOutletContext<{
        onNext?: (skipOtp?: boolean) => void;
        onBack?: () => void;
    } | null>();
    const onNext = context?.onNext || (() => {});
    const onBack = () => {
        navigate('/select-account-type');
    };

    const [localData, setLocalData] = useState<PersonalInfoData>({
        uuid: firstStep?.data?.uuid || '',
        name: firstStep?.data?.step_1?.name || '',
        email: firstStep?.data?.step_1?.email || '',
        provider: firstStep?.data?.step_1?.provider || 'normal',
        document_type: firstStep?.data?.step_1?.document_type || 'CC',
        docId: firstStep?.data?.step_1?.document_number || '',
        phone: firstStep?.data?.step_1?.phone_number || '',
        nationality: firstStep?.data?.step_1?.nationality || '',
        address: firstStep?.data?.step_1?.address || '',
    });

    useEffect(() => {
        if (firstStep?.data?.step_1) {
            setLocalData({
                uuid: firstStep.data.uuid || '',
                name: firstStep.data.step_1.name || '',
                email: firstStep.data.step_1.email || '',
                provider: firstStep.data.step_1.provider || 'normal',
                document_type: firstStep.data.step_1.document_type || 'CC',
                docId: firstStep.data.step_1.document_number || '',
                phone: firstStep.data.step_1.phone_number || '',
                nationality: firstStep.data.step_1.nationality || '',
                address: firstStep.data.step_1.address || '',
            });
        }
    }, [firstStep]);

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    name: validationErrors['name'] || [],
                    email: validationErrors['email'] || [],
                    document_type: validationErrors['document_type'] || [],
                    docId: validationErrors['document_number'] || [],
                    phone: validationErrors['phone_number'] || [],
                    nationality: validationErrors['nationality'] || [],
                    address: validationErrors['address'] || [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const translations = {
        title: t('personal-info-form.title'),
        prev: t('navigation.prev'),
        next: t('navigation.next'),
        nameLabel: t('personal-info-form.name.label'),
        emailLabel: t('personal-info-form.email.label'),
        docTypeLabel: t('personal-info-form.docType.label'),
        docIdLabel: t('personal-info-form.docId.label'),
        phoneLabel: t('personal-info-form.phone.label'),
        nationalityLabel: t('personal-info-form.nationality.label'),
        addressLabel: t('personal-info-form.address.label'),
        nameRequiredError: t('personal-info-form.name.required-error'),
        emailRequiredError: t('personal-info-form.email.required-error'),
        docTypeRequiredError: t('personal-info-form.docType.required-error'),
        docIdRequiredError: t('personal-info-form.docId.required-error'),
        phoneRequiredError: t('personal-info-form.phone.required-error'),
        nationalityRequiredError: t('personal-info-form.nationality.required-error'),
        addressRequiredError: t('personal-info-form.address.required-error'),
    };

    const handleChange = (field: keyof PersonalInfoData, value: string) => {
        const updated = { ...localData, [field]: value as any };
        setLocalData(updated);

        if (setFirstStep && firstStep?.data?.step_1) {
            setFirstStep({
                data: {
                    ...firstStep.data,
                    step_1: {
                        ...firstStep.data.step_1,
                        [field === 'docId' ? 'document_number' : field === 'phone' ? 'phone_number' : field]: value,
                    },
                },
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: PersonalInfoDataErrors = {
            name: localData.name ? [] : [translations.nameRequiredError],
            email: localData.email ? [] : [translations.emailRequiredError],
            document_type: localData.document_type ? [] : [translations.docTypeRequiredError],
            docId: localData.docId ? [] : [translations.docIdRequiredError],
            phone: localData.phone ? [] : [translations.phoneRequiredError],
            nationality: localData.nationality ? [] : [translations.nationalityRequiredError],
            address: localData.address ? [] : [translations.addressRequiredError],
        };

        if (localData.email != '' && !isValidEmail(localData.email)) {
            newErrors.email.push(t('personal-info-form.email.invalid-error'));
        }
        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((fieldErrors) => fieldErrors.length > 0);

        return hasErrors ? false : true;
    };

    const submitForm = async () => {
        if (!validateForm()) {
            return;
        }

        requestRegisterFirstStep(
            {
                name: localData.name,
                email: localData.email,
                provider: localData.provider as 'normal' | 'google',
                document_type: localData.document_type,
                document_number: localData.docId,
                phone_number: localData.phone,
                nationality: localData.nationality,
                user_type_id: selectedRole?.id as 2 | 3,
                address: localData.address,
            },
            { onNext }
        );
    };

    return (
        <Form formRef={formRef} onSubmit={submitForm} classes="d-flex flex-column gap-2">
            <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                {translations.title}
            </Typography>

            <Input
                data-testid="name-input"
                variant={InputVariants.labeledInput}
                type={InputTypes.text}
                label={`${translations.nameLabel}*`}
                value={localData.name}
                onChange={(value) => handleChange('name', value)}
                state={errors.name.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.name}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.email}
                label={`${translations.emailLabel}*`}
                value={localData.email}
                onChange={(value) => handleChange('email', value)}
                state={errors.email.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.email}
            />
            <LabeledInput
                type={InputTypes.select}
                label={`${translations.docTypeLabel}*`}
                options={documentTypeOptions}
                value={localData.document_type}
                onChange={(value) => handleChange('document_type', value as PersonalInfoData['document_type'])}
                state={errors.document_type.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.document_type}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.text}
                label={`${translations.docIdLabel}*`}
                value={localData.docId}
                onChange={(value) => handleChange('docId', value)}
                state={errors.docId.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.docId}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.tel}
                label={`${translations.phoneLabel}*`}
                value={localData.phone}
                onChange={(value) => handleChange('phone', value)}
                state={errors.phone.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.phone}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.text}
                label={`${translations.nationalityLabel}*`}
                value={localData.nationality}
                onChange={(value) => handleChange('nationality', value)}
                state={errors.nationality.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.nationality}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.textarea}
                label={`${translations.addressLabel}*`}
                value={localData.address}
                onChange={(value) => handleChange('address', value)}
                state={errors.address.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.address}
            />

            <Button onClick={onBack} label={translations.prev} mode={ButtonMode.primary} outline />
            <Button label={translations.next} mode={ButtonMode.primary} onClick={submitForm} />
        </Form>
    );
};
